import BaseModel from "../models/BaseModel";
import BaseModelMap from "../models/BaseModelMap";
import BaseObject from "../models/BaseObject";

export type PersistentObjectData = Record<string, any>;

export interface UndoOperation {
  readonly kind: string;
  readonly objectId?: string;
  readonly object: BaseObject;
  undo(): void;
  redo(): void;
}

export function clonePersistentData(data: PersistentObjectData): PersistentObjectData {
  return structuredClone(data);
}

export class ChangeObjectOperation implements UndoOperation {
  readonly kind = "changeObject";
  readonly objectId: string;

  constructor(
    public readonly object: BaseObject,
    private snapshot: PersistentObjectData,
  ) {
    this.objectId = object.id;
  }

  private swap() {
    const current = clonePersistentData(this.object.createUndoSnapshot());
    this.object.restoreUndoSnapshot(this.snapshot);
    this.snapshot = current;
    this.object.raiseOnChange();
  }

  undo() {
    this.swap();
  }

  redo() {
    this.swap();
  }
}

export class NewObjectOperation implements UndoOperation {
  readonly kind = "newObject";
  readonly objectId: string;

  constructor(public readonly object: BaseModel) {
    this.objectId = object.id;
  }

  undo() {
    this.object.setDeletedForUndo(true);
  }

  redo() {
    this.object.setDeletedForUndo(false);
  }
}

export class DeleteObjectOperation implements UndoOperation {
  readonly kind = "deleteObject";
  readonly objectId: string;

  constructor(
    public readonly object: BaseModel,
    private snapshot: PersistentObjectData,
  ) {
    this.objectId = object.id;
  }

  undo() {
    this.object.setDeletedForUndo(false);
    this.object.restorePersistentData(this.snapshot);
    this.object.raiseOnChange("@undelete");
  }

  redo() {
    this.snapshot = clonePersistentData(this.object.toPersistentData());
    this.object.setDeletedForUndo(true);
  }
}

abstract class CollectionOperation implements UndoOperation {
  private readonly originalParent: BaseObject | undefined;
  abstract readonly kind: "addToCollection" | "removeFromCollection";
  abstract undo(): void;
  abstract redo(): void;
  readonly objectId: string;
  readonly collectionId: string;

  constructor(
    protected readonly collection: BaseModelMap<BaseModel>,
    public readonly object: BaseModel,
    protected readonly index: number,
  ) {
    this.objectId = object.id;
    this.collectionId = collection.id;
    this.originalParent = object.parent;
  }

  protected add() {
    const entries = Array.from(this.collection.items.entries()).filter(([, item]) => item !== this.object);
    const insertionIndex = Math.max(0, Math.min(this.index, entries.length));
    entries.splice(insertionIndex, 0, [this.object.key, this.object]);
    const orderedItems: BaseModel[] = [];
    for (const entry of entries) orderedItems.push(entry[1]);
    this.collection.replaceItems(orderedItems);
    this.object.parent = this.originalParent;
    this.collection.raiseOnChange("childCollection");
    this.collection.parent?.raiseOnChange("childCollection");
  }

  protected remove() {
    this.collection.remove(this.object);
    this.collection.raiseOnChange("childCollection");
    this.collection.parent?.raiseOnChange("childCollection");
  }
}

export class AddToCollectionOperation extends CollectionOperation {
  readonly kind = "addToCollection" as const;

  undo() {
    this.remove();
  }

  redo() {
    this.add();
  }
}

export class RemoveFromCollectionOperation extends CollectionOperation {
  readonly kind = "removeFromCollection" as const;

  undo() {
    this.add();
  }

  redo() {
    this.remove();
  }
}
